import { useCallback } from 'react'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import type { DataSource } from '@/src/renderer/lib/dataSource/dataSource'
import { formatImportedFrom } from '@/src/renderer/lib/import/importedFrom'
import { keepingUndo } from '@/src/renderer/lib/dataSource/withUndoForget'
import { SELF_COMPLETING_STEPS, type IntroStepId, type PageHelpId } from './steps'

/*
  Merker der **Einführung** — je Gerät in der Tabelle `settings`, nie synchronisiert (Sven, 13.10.2026:
  „pro Gerät“). Einzelne Schlüssel statt eines Objekts: Seitenleiste, Seiten und das Fenster der
  Schnelleingabe schreiben gleichzeitig, und ein ganzes Objekt überschriebe den Teil der anderen.
*/

/**
 * `none` — kein frisches Onboarding auf diesem Gerät (auch: wer schon vor der Einführung fertig war);
 * es gibt nur die Seitenhilfe. `new` — die Folien sind gerade durch, das Hauptfenster startet die
 * Einführung. `running` — Beispielprojekt, Angebot und „Entdecken“ sind da.
 */
export type IntroStatus = 'none' | 'new' | 'running'

/** Das Angebot der Lernaufgaben in Kurz notiert. */
export type IntroOffer = 'none' | 'pending' | 'accepted' | 'declined'

export const INTRO_SETTING = {
  status: 'intro.status',
  offer: 'intro.offer',
  /** Was man schon einmal getan hat — hakt „Entdecken“ ab. */
  discovered: 'intro.discovered',
  /** Seiten, deren Seitenhilfe einmal offen war; ihr ? ist danach nicht mehr getönt. */
  helpSeen: 'intro.helpSeen',
  discoverHidden: 'intro.discoverHidden',
  discoverCollapsed: 'intro.discoverCollapsed',
} as const

const NO_STEPS: readonly IntroStepId[] = []
const NO_PAGES: readonly PageHelpId[] = []

/**
 * Herkunft (`importedFrom`) einer Lernaufgabe — darüber findet die Einführung sie wieder. Mit `run`, weil
 * „Einführung neu starten“ dieselbe Aufgabe noch einmal anlegen kann und `findImported` je Marke nur eine
 * Zeile kennt.
 */
export function learnTaskOrigin(step: IntroStepId, run?: string): string {
  return formatImportedFrom('intro', run ? `learn:${step}:${run}` : `learn:${step}`)
}

/** Gehört die Herkunft zur Lernaufgabe dieses Schritts (mit oder ohne Lauf)? */
export function isLearnTaskOf(origin: string, step: IntroStepId): boolean {
  const base = learnTaskOrigin(step)
  return origin === base || origin.startsWith(`${base}:`)
}

export function useIntroStatus(): { status: IntroStatus; loaded: boolean } {
  const { value, loaded } = useDataSource().useSetting<IntroStatus>(INTRO_SETTING.status, 'none')
  return { status: value, loaded }
}

/** Das Angebot der Lernaufgaben — `undefined`, bis der Merker gelesen ist (sonst blitzte, was ohne Angebot steht). */
export function useIntroOffer(): IntroOffer | undefined {
  const { value, loaded } = useDataSource().useSetting<IntroOffer>(INTRO_SETTING.offer, 'none')
  return loaded ? value : undefined
}

export function useDiscoveredSteps(): readonly IntroStepId[] {
  return useDataSource().useSetting<readonly IntroStepId[]>(INTRO_SETTING.discovered, NO_STEPS).value
}

/** Seitenhilfe: ob ihr ? schon einmal offen war, und das Merken. */
export function usePageHelpSeen(page: PageHelpId): { seen: boolean; loaded: boolean; markSeen: () => void } {
  const source = useDataSource()
  const { value, loaded } = source.useSetting<readonly PageHelpId[]>(INTRO_SETTING.helpSeen, NO_PAGES)
  const markSeen = useCallback(() => { void addToList(source, INTRO_SETTING.helpSeen, page) }, [source, page])
  return { seen: value.includes(page), loaded, markSeen }
}

// Lesen-Ändern-Schreiben nacheinander: Die Schnelleingabe meldet zwei Schritte auf einmal, und parallel
// überschriebe der zweite den ersten.
let pending: Promise<unknown> = Promise.resolve()
function inTurn<T>(work: () => Promise<T>): Promise<T> {
  const next = pending.then(work, work)
  pending = next.catch(() => undefined)
  return next
}

function addToList<T>(source: DataSource, key: string, item: T): Promise<boolean> {
  return inTurn(async () => {
    // Frisch gelesen statt aus dem Hook: Ein anderes Fenster kann inzwischen etwas dazugeschrieben haben.
    const list = await source.getSetting<readonly T[]>(key, [])
    if (list.includes(item)) return false
    await source.setSetting(key, [...list, item])
    return true
  })
}

/** Die Folien sind durch: Das Hauptfenster startet die Einführung (`OnboardingPage`). */
export function markIntroNew(source: DataSource): Promise<void> {
  return source.setSetting<IntroStatus>(INTRO_SETTING.status, 'new')
}

/**
 * Jemand hat einen Schritt einmal getan (Schnelleingabe, Lebensbereich gewechselt, Pinnwand geöffnet …).
 * Merkt ihn für „Entdecken“ und hakt die passende Lernaufgabe ab, falls sie noch offen ist. Gemeldet wird
 * immer, auch ohne laufende Einführung — sichtbar wird es nur dort, wo sie läuft.
 */
export async function noteIntroStep(source: DataSource, step: IntroStepId): Promise<void> {
  await addToList(source, INTRO_SETTING.discovered, step)
  if (!SELF_COMPLETING_STEPS.includes(step)) return
  if ((await source.getSetting<IntroOffer>(INTRO_SETTING.offer, 'none')) !== 'accepted') return
  await completeLearnTask(source, step)
}

async function completeLearnTask(source: DataSource, step: IntroStepId): Promise<void> {
  const { tasks } = await source.findImported('intro')
  for (const [origin, row] of tasks) {
    if (row.deleted || !isLearnTaskOf(origin, step)) continue
    const task = await source.getTask(row.id)
    if (!task || task.completedAt != null) continue
    // Still, ohne Geschafft-Moment und ohne der Handlung ihr Rückgängig zu nehmen: Die war woanders.
    await keepingUndo(() => source.updateTask(task.id, { status: 'completed', completedAt: new Date() }))
  }
}

/** `note(step)` für Stellen in der Oberfläche; Fehler bleiben stumm, die Handlung selbst ist schon passiert. */
export function useIntroNote(): (step: IntroStepId) => void {
  const source = useDataSource()
  return useCallback((step: IntroStepId) => {
    noteIntroStep(source, step).catch(error => console.warn('[intro] note failed', step, error))
  }, [source])
}

/** Die Karte **Entdecken**: sichtbar, solange die Einführung läuft und niemand sie ausgeblendet hat. */
export function useDiscoverCard(): {
  visible: boolean
  collapsed: boolean
  discovered: readonly IntroStepId[]
  setCollapsed: (collapsed: boolean) => void
  hide: () => void
} {
  const source = useDataSource()
  const { status } = useIntroStatus()
  const hidden = source.useSetting<boolean>(INTRO_SETTING.discoverHidden, false)
  const collapsed = source.useSetting<boolean>(INTRO_SETTING.discoverCollapsed, false)
  const discovered = useDiscoveredSteps()
  const { setValue: setCollapsedValue } = collapsed
  const { setValue: setHidden } = hidden
  const setCollapsed = useCallback((next: boolean) => { void setCollapsedValue(next) }, [setCollapsedValue])
  const hide = useCallback(() => { void setHidden(true) }, [setHidden])
  return {
    visible: status === 'running' && hidden.loaded && !hidden.value,
    collapsed: collapsed.value,
    discovered,
    setCollapsed,
    hide,
  }
}
